import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import { FC, useRef, useState } from 'react';
import styles from '../../Tooltip/Tooltip.module.css';
import { TooltipProps } from '../../Tooltip/Tooltip.types';
import iconStyles from '../Icon.module.css';

const StarTooltip: FC<TooltipProps> = ({ content, position, className }) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    placement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: position,
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  return (
    <>
      <svg
        width="23"
        height="23"
        viewBox="0 0 23 23"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        ref={reference}
        className={classNames(iconStyles.starTooltip, { [className!]: className !== undefined })}
        {...getReferenceProps()}
        data-testid="star-tooltip-icon"
      >
        <path
          d="M0.597656 12C0.597656 18.0751 5.52252 23 11.5977 23C17.6728 23 22.5977 18.0751 22.5977 12V11.5C22.5977 5.14873 17.4489 0 11.0977 0H4.99766C2.5676 0 0.597656 1.96995 0.597656 4.4L0.597656 12Z"
          fill="#E21216"
        />
        <path
          d="M11.5983 4.33594L13.6583 8.50927L18.265 9.1826L14.9316 12.4293L15.7183 17.0159L11.5983 14.8493L7.47831 17.0159L8.26497 12.4293L4.93164 9.1826L9.53831 8.50927L11.5983 4.33594Z"
          stroke="white"
          strokeWidth="1.33333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <FloatingPortal>
        {open && (
          <div
            data-testid="floating-root-ui"
            ref={floating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content',
            }}
            className={styles.tooltipContainer}
            {...getFloatingProps()}
          >
            <div
              ref={arrowRef}
              className={styles.tooltipArrow}
              style={{
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide!]: '-4px',
              }}
            />
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export default StarTooltip;
